import React, { useReducer } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import BundleSelect from '../../components/BundleSelect/BundleSelect';
import BundleOption from '../../components/BundleSelect/BundleOption';
import Faq from '../../components/Faqs/Faq';
import BundleSellingPoints from '../../components/BundleSellingPoints/BundleSellingPoints';
import BundleHero from '../../components/BundleHero/BundleHero';
import BundleSocialProof from '../../components/BundleSocialProof/BundleSocialProof';
import FeatureBlock, { variants as FeatureBlockVariants } from '../../components/FeatureOverview/FeatureBlock';

import orderBundleRequest from '../../services/orderBundle';
import { initiatePaymentUrl } from '../../constants/urls';
import requestStatus from '../../constants/requestStatus';
import { EnvironmentProvider } from '../../contexts/EnvironmentContext';
import { getInitialState, reducer, CustomError } from './reducer';
import {
  ErrorInpage,
  PageElement,
  PrimaryButton,
  TextRegular,
  Title1,
  Title2,
  Title3,
  UtilsBlock,
} from '@hz-design-system/web-ui';
import { AppState, EnvironmentConfig } from '../../../../../types/AppState';

const PurchaseBundleApp = ({ appState, environment }: { appState: AppState; environment: EnvironmentConfig }) => {
  const {
    dataObject: { tablePurchaseBundle, faqs },
    seoBlurb,
  } = appState;
  const { xsrfToken, loggedInUser } = environment;

  const [state, dispatch] = useReducer(reducer, getInitialState());
  const i18n = useI18nContext();

  const orderBundle = async ({ bundleType }: { bundleType: string }) => {
    try {
      dispatch({ type: 'initiate' });
      const { orderId } = await orderBundleRequest({ xsrfToken, bundleType });
      dispatch({ type: 'success', payload: orderId });
      return window.location.assign(initiatePaymentUrl(orderId));
    } catch (err) {
      dispatch({ type: 'failure', payload: err as CustomError });
    }
  };

  const redirectToLogInScreen = () =>
    window.location.assign(`/account/login.html?target=${encodeURIComponent(window.location.href)}`);

  const hasSocialProofContent = i18n.tExists('purchaseBundle.socialProof.title');

  return (
    <EnvironmentProvider value={environment}>
      <div className="hz-PurchaseBundle">
        <PageElement kind="full-width" className="hz-PurchaseBundle-section hz-PurchaseBundle-hero">
          <div className="hz-PurchaseBundle-container">
            <BundleHero />
          </div>
        </PageElement>
        <PageElement kind="full-width" className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <BundleSellingPoints />
          </div>
        </PageElement>
        <PageElement kind="full-width" className="hz-PurchaseBundle-section hz-PurchaseBundle-bundles">
          <div id="hz-PurchaseBundle-bundles" className="hz-PurchaseBundle-container">
            <div className="hz-PurchaseBundle-wrapBundles">
              <div className="hz-PurchaseBundle-bundlesHeaderWrap">
                <Title1>{i18n.t('purchaseBundle.bundles.header_line_1')}</Title1>
                <TextRegular>{i18n.t('purchaseBundle.bundles.header_line_2')}</TextRegular>
              </div>
              {state.status === requestStatus.failure && state.error && (
                <ErrorInpage
                  icon="alert"
                  description={
                    // eslint-disable-next-line react/no-danger
                    (<span dangerouslySetInnerHTML={{ __html: i18n.t(state.error) }} />) as unknown as string
                  }
                />
              )}
              <BundleSelect>
                {tablePurchaseBundle.map((bundle) => (
                  <BundleOption
                    key={bundle.bundleType}
                    bundleType={bundle.bundleType}
                    isBundleRecommended={bundle.isBundleRecommended}
                    bundleLabel={bundle.bundleLabel}
                    marketFitText={bundle.marketFitText}
                    isDisabled={state.status === requestStatus.fetching}
                    onSelect={loggedInUser ? orderBundle : redirectToLogInScreen}
                    originalPrice={bundle.originalPrice}
                    effectivePrice={bundle.effectivePrice}
                    features={bundle.features}
                    isPriceDiscounted={bundle.isPriceDiscounted}
                    discountPercentage={bundle.discountPercentage}
                    isCurrentActiveBundle={bundle.isCurrentActiveBundle}
                  />
                ))}
              </BundleSelect>
              <div className="hz-PurchaseBundles-wrapBundles--disclaimer">
                <p>{i18n.t('purchaseBundle.bundleDisclaimers.item1')}</p>
                <div className="hz-PurchaseBundle-disclaimerAsterisks">
                  <p>{i18n.t('purchaseBundle.bundleDisclaimers.item2')}</p>
                  <p>{i18n.t('purchaseBundle.bundleDisclaimers.item3')}</p>
                </div>
              </div>
            </div>
          </div>
        </PageElement>

        <PageElement kind="full-width" className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <div className="hz-PurchaseBundle-wrapBundles">
              <div className="hz-PurchaseBundle-featureOverview">
                <UtilsBlock marginBottom="l">
                  <Title2>
                    <Trans i18nKey="purchaseBundle.featureOverview.header" />
                  </Title2>
                </UtilsBlock>
                <FeatureBlock
                  variant={FeatureBlockVariants.verifiedSeller}
                  title={i18n.t('purchaseBundle.featureOverview.verifiedSeller.title')}
                  body={i18n.t('purchaseBundle.featureOverview.verifiedSeller.body')}
                />
                <FeatureBlock
                  variant={FeatureBlockVariants.sellerPage}
                  title={i18n.t('purchaseBundle.featureOverview.sellerPage.title')}
                  body={i18n.t('purchaseBundle.featureOverview.sellerPage.body')}
                />
                <FeatureBlock
                  variant={FeatureBlockVariants.fotoknaller}
                  title={i18n.t('purchaseBundle.featureOverview.fotoknaller.title')}
                  body={i18n.t('purchaseBundle.featureOverview.fotoknaller.body')}
                />
                <FeatureBlock
                  isPlus
                  variant={FeatureBlockVariants.opvalsticker}
                  title={i18n.t('purchaseBundle.featureOverview.opvalsticker.title')}
                  body={i18n.t('purchaseBundle.featureOverview.opvalsticker.body')}
                />
                <FeatureBlock
                  isPlus
                  variant={FeatureBlockVariants.gallery}
                  title={i18n.t('purchaseBundle.featureOverview.gallery.title')}
                  body={i18n.t('purchaseBundle.featureOverview.gallery.body')}
                />
              </div>
            </div>
          </div>
        </PageElement>
        {hasSocialProofContent && (
          <PageElement kind="full-width" className="hz-PurchaseBundle-section">
            <div className="hz-PurchaseBundle-container">
              <BundleSocialProof />
            </div>
          </PageElement>
        )}
        <PageElement kind="full-width" className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <div className="hz-PurchaseBundle-faqsWrap">
              <UtilsBlock marginBottom="s">
                <Title2>{i18n.t('purchaseBundle.faqs.header')}</Title2>
              </UtilsBlock>
              {faqs.map((faq, index) => (
                <Faq key={`faq-${index}`} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </PageElement>
        <PageElement kind="full-width" className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <div className="hz-PurchaseBundle-bottomCta">
              <UtilsBlock marginBottom="s">
                <Title3>{i18n.t('purchaseBundle.ctas.bottomCta.message')}</Title3>
              </UtilsBlock>
              <PrimaryButton as="a" href="#hz-PurchaseBundle-bundles">
                {i18n.t('purchaseBundle.ctas.bottomCta.buttonAction')}
              </PrimaryButton>
            </div>
          </div>
        </PageElement>
        <PageElement kind="full-width" className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <TextRegular>{seoBlurb}</TextRegular>
          </div>
        </PageElement>
      </div>
    </EnvironmentProvider>
  );
};

export default PurchaseBundleApp;
